import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { useTheme } from '../../contexts/ThemeContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import DrivstoffChart from './DrivstoffChart'
import { FUEL_TOPS, changeColor, fuelColor, int, pct, signed, withEkskluder } from './drivstoff'
import type { DrivstoffOversikt as Oversikt, DrivstoffTidsserie, FuelCategoryStats, FuelTop, Periods, TopBrand, TopModel } from './types'

/**
 * Teknisk › Drivstoff › Oversikt. `queryKey` is the applied query as a JSON string;
 * `ekskluderElbil` is the page-level switch, sent as `ekskluder_elbil=true` (AC5).
 */
export default function DrivstoffOversikt({ queryKey, ekskluderElbil, onSeeAll, onPeriods }: {
  queryKey: string
  ekskluderElbil: boolean
  onSeeAll: (tab: 'merke' | 'modell', kategori: FuelTop) => void
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
}) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const [oversikt, setOversikt] = useState<Oversikt | null>(null)
  const [series, setSeries] = useState<DrivstoffTidsserie | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [topChoice, setTopChoice] = useState<FuelTop>('elbil')

  useLatest(alive => {
    const params = withEkskluder(JSON.parse(queryKey), ekskluderElbil)
    setLoading(true)
    setError(null)
    Promise.all([publicAPI.getDrivstoffOversikt(params), publicAPI.getDrivstoffTidsserie(params)])
      .then(([o, s]) => {
        if (!alive()) return
        setOversikt(o.data)
        setSeries(s.data)
        onPeriods(o.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('drivstoff.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, ekskluderElbil])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!oversikt || !series) return null

  if (oversikt.total.count === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{t('drivstoff.noData')}</p>
  }

  // Elbil is not in the response when it is excluded
  const byKey = Object.fromEntries(oversikt.kategorier.map(k => [k.kategori, k])) as Partial<Record<FuelTop, FuelCategoryStats>>
  const label = (k: FuelTop) => t(`drivstoff.category.${k}` as const)
  const topKategori: FuelTop = ekskluderElbil && topChoice === 'elbil' ? 'hybrid' : topChoice
  const noComparison = (
    <span className="text-gray-400" title={t('drivstoff.noComparisonTitle')}>{t('drivstoff.vsLastYear')}: –</span>
  )
  const swatch = (k: FuelTop) => (
    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: fuelColor(k, isDark) }} aria-hidden="true" />
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="drivstoff-kpi">
        {FUEL_TOPS.map(k => {
          const row = byKey[k]
          if (!row) {
            return (
              <div key={k} className="card p-4 opacity-60" data-testid={`drivstoff-kpi-${k}`}>
                <p className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">{swatch(k)}{label(k)}</p>
                <p className="text-2xl font-bold text-gray-500 dark:text-gray-400 mt-1" data-testid={`drivstoff-share-${k}`}>
                  {t('drivstoff.excluded')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('drivstoff.excludedHint')}</p>
              </div>
            )
          }
          return (
            <div key={k} className="card p-4" data-testid={`drivstoff-kpi-${k}`}>
              <p className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">{swatch(k)}{label(k)}</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1" data-testid={`drivstoff-share-${k}`}>
                {pct(row.share)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('drivstoff.countCars', { count: int(row.count) })}</p>
              <p className="text-sm mt-1" data-testid={`drivstoff-change-${k}`}>
                {row.share_change_pp === null
                  ? noComparison
                  : <span className={changeColor(row.share_change_pp)}>{t('drivstoff.ppVsLastYear', { value: signed(row.share_change_pp) })}</span>}
              </p>
              {row.underkategorier.length > 0 && (
                <ul className="mt-2 pt-2 border-t border-gray-200 dark:border-dark-border text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                  {row.underkategorier.map(u => (
                    <li key={u.kategori} className="flex justify-between gap-2" data-testid={`drivstoff-kpi-${u.kategori}`}>
                      <span>{t(`drivstoff.category.${u.kategori}` as const)}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{pct(u.share)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
        <div className="card p-4" data-testid="drivstoff-kpi-total">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {ekskluderElbil ? t('drivstoff.totalWithoutElbil') : t('drivstoff.total')}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-1">{int(oversikt.total.count)}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('drivstoff.totalSubtitle')}</p>
          <p className="text-sm mt-1" data-testid="drivstoff-change-total">
            {oversikt.total.change_pct === null
              ? noComparison
              : <span className={changeColor(oversikt.total.change_pct)}>{t('drivstoff.pctVsLastYear', { value: signed(oversikt.total.change_pct) })}</span>}
          </p>
        </div>
      </div>

      <DrivstoffChart series={series} />

      <div className="card p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('drivstoff.leasingLoanTitle')}</h3>
        <div className="overflow-x-auto">
          <table className="data-table data-table-compact" data-testid="drivstoff-leasing-loan">
            <thead>
              <tr>
                <th></th>
                {FUEL_TOPS.map(k => <th key={k} className="text-right">{label(k)}</th>)}
              </tr>
            </thead>
            <tbody>
              {([
                ['leasing', t('drivstoff.leasingShare')],
                ['loan', t('drivstoff.loanShare')],
              ] as const).map(([kind, rowLabel]) => (
                <tr key={kind}>
                  <td>{rowLabel}</td>
                  {FUEL_TOPS.map(k => {
                    const row = byKey[k]
                    return (
                      <td key={k} className="text-right">
                        {row ? pct(kind === 'leasing' ? row.leasing_share : row.loan_share) : '–'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('drivstoff.topTitle')}</h3>
          <div className="flex flex-wrap gap-2" role="group" data-testid="drivstoff-top-kategori">
            {FUEL_TOPS.map(k => {
              const disabled = !byKey[k]
              return (
                <button
                  key={k}
                  onClick={() => setTopChoice(k)}
                  disabled={disabled}
                  aria-pressed={topKategori === k}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    disabled
                      ? 'bg-gray-100 dark:bg-dark-card text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : topKategori === k
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
                  }`}
                >
                  {swatch(k)}{label(k)}
                </button>
              )
            })}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {(['models', 'brands'] as const).map(view => {
            const list: (TopModel | TopBrand)[] = (view === 'models' ? oversikt.top_models : oversikt.top_brands)[topKategori] ?? []
            return (
              <div key={view} className="card p-4" data-testid={view === 'models' ? 'drivstoff-top-models' : 'drivstoff-top-brands'}>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {view === 'models' ? t('drivstoff.topModels') : t('drivstoff.topBrands')} – {label(topKategori)}
                </h4>
                <div className="overflow-x-auto">
                  <table className="data-table data-table-compact">
                    <thead>
                      <tr>
                        <th className="w-8">#</th>
                        <th>{view === 'models' ? t('drivstoff.colModel') : t('drivstoff.colBrand')}</th>
                        <th className="text-right">{t('drivstoff.colCount')}</th>
                        <th className="text-right">{t('drivstoff.colShare', { category: label(topKategori) })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((item, i) => (
                        <tr key={'master_model_id' in item ? item.master_model_id : `${item.brand}-${i}`}>
                          <td>{i + 1}</td>
                          <td>
                            {'model' in item
                              ? <><span className="text-gray-500 dark:text-gray-400">{item.brand}</span> {item.model}</>
                              : item.brand}
                          </td>
                          <td className="text-right">{int(item.count)}</td>
                          <td className="text-right">{pct(item.share)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => onSeeAll(view === 'models' ? 'modell' : 'merke', topKategori)}
                  className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {t('drivstoff.seeAll')} →
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
